﻿import $ from 'jquery';
import { Point, ElementPosition } from "../utilities/elementposition";
import Focus from "../utilities/focusfield";
import KeyCode from "../utilities/keycode";
import _ from 'underscore';

export class Modal {
  private DEFAULTS = {
    $element: null,
    alignment: null,
    content: null
  };
  private static modalStack: ModalStack;
  private static $globalOverlay: JQuery = null;
  private id: string;
  private $modal: JQuery;
  private isOpen: boolean = false;
  private options: ModalOptions;
  private previousPosition: Point;
  private previousTitle: string;
  private previousPrimaryCaption: string;

  public get position() {
    if (!this.$modal)
      return null;

    return this.$modal.position();
  }

  public get title() : string {
    if (!this.$modal)
      return null;

    return this.$modal.find('.modal-title').text();
  }

  public get primaryCaption(): string {
    if (!this.$modal)
      return null;

    return this.$modal.find('.modal-actions .button-primary').text();
  }

  private get isDismissableFromOverlay(): boolean {
    return this.$modal.find('[data-dismiss-from-overlay]').length > 0;
  }

  constructor(options: ModalOptions) {
    this.options = $.extend(this.DEFAULTS, options, $(options.$element).data());
    this.validateOptions();
    this.id = $(this.options.content).data('modal-id');
  }

  public rememberPosition(position: Point): void {
    this.previousPosition = position;
  }

  public rememberTitle(value: string): void {
    this.previousTitle = value;
  }

  public rememberPrimaryCaption(value: string): void {
    this.previousPrimaryCaption = value;
  }

  private validateOptions = function () {
    if (!this.options.content || this.options.content.length == 0)
      throw new Error('Rustig.Modal.open requires you provide an options object with a content property');
    if (!$(this.options.content).data('modal-id'))
      throw new Error('Rustig.Modal.open requires you provide content with the root element containing a data-modal-id attribute');
  }
  private open(): void {
    if (this.isOpen)
      return;

    this.buildOverlayIfNeeded();
    this.buildModalAndAddToDOM();
    this.moveOverlayBehindTopMostModal();
    this.positionModal();

    this.$modal.trigger('show.rustig.modal');

    Modal.$globalOverlay.show();

    this.$modal.show();
    this.isOpen = true;
    this.setDefaultFocus();

    this.$modal.trigger('shown.rustig.modal');
    if (this.options.onShown)
      this.options.onShown(this.$modal);
  }
  private close(): void {
    if (this.isOpen == false)
      return;

    this.$modal.trigger('hide.rustig.modal');
    $(document).off('focusin.rustig.modal');

    this.removeModal();
    this.hideOrMoveOverlay();
    this.isOpen = false;

    $(document).trigger('hidden.rustig.modal', this.$modal);
    if (this.options.onHidden)
      this.options.onHidden(this.$modal);
  }
  private performDefault(): boolean {
    if (this.isOpen == false)
      return;

    let $defaultButton = this.$modal.find('[data-default]');
    if ($defaultButton.length != 0) {
      $defaultButton.click();
      return true;
    }
    return false;
  }
  private buildOverlayIfNeeded() {
    if (Modal.$globalOverlay)
      return;

    Modal.$globalOverlay = $('<div>').attr('id', 'modalGlobalOverlay');
    $(document.body).append(Modal.$globalOverlay);
  }
  private buildModalAndAddToDOM(): void {
    this.$modal = $('<div>').addClass('modal-container');
    $(document.body).append(this.$modal);
    if ($.fn.draggable) {
      let m: any = this.$modal;
      m.draggable({
        handle: '.modal-title',
        start: function (e) {
          $(e.target).trigger('dragstart.rustig.modal');
        }
      });
    }

    let $content = $(this.options.content).clone(true);

    if ($content.is('modal-preserve-context'.asDataSelector())) {
      $content.find('.modal-title').text(this.previousTitle);
      $content.find('.modal-actions .button-primary').text(this.previousPrimaryCaption);
    }

    let containerClass = $content.data('container-class');
    if (containerClass)
      this.$modal.addClass(containerClass);

    let modalContent = $content[0].outerHTML + this.getSiblingContent();
    let focusGuard = '<div data-keyboard-focus-bound="on" tabindex="0"></div>';
    modalContent = focusGuard + modalContent + focusGuard;
    this.$modal.html(modalContent);

    Modal.modalStack.push(this);
  }
  private getSiblingContent(): string {
    let siblingContent = '';
    let canGetSiblings = this.options.content.siblings || $(this.options.content).siblings;
    if (canGetSiblings) {
      $(this.options.content).siblings().not('[data-modal-id]').each(function () {
        siblingContent += '\r\n' + $(this)[0].outerHTML;
      });
    }
    return siblingContent;
  }
  private removeModal(): void {
    this.$modal.remove();
    Modal.modalStack.pop();
  }
  private positionModal() {
    let position = new ElementPosition(this.$modal);
    let hasElement = this.options.$element != null;
    let hasVisibleElement = hasElement && this.options.$element.is(':visible');
    let lastTargetPosition = hasElement ? this.options.$element.data('rustig.lastoffset') : null;
    let canPositionByPoint = !hasVisibleElement && lastTargetPosition;
    let canPositionByElement = hasVisibleElement;

    if (this.previousPosition) {
      position.setPositionByPoint(this.previousPosition);
      this.previousPosition = null;
    }
    else if (canPositionByElement) {
      position.setPositionByElement(this.options.$element, this.options.alignment);
    }
    else if (canPositionByPoint) {
      position.setPositionByPoint(lastTargetPosition);
    }
    else {
      position.setPositionCenterScreen();
    }
  }
  private hideOrMoveOverlay(): void {
    if (Modal.modalStack.isEmpty) {
      Modal.$globalOverlay.hide();
    }
    else {
      this.moveOverlayBehindTopMostModal();
    }
  }
  private moveOverlayBehindTopMostModal(): void {
    let topMostModal = Modal.modalStack.peek();
    if (topMostModal)
      Modal.$globalOverlay.detach().insertBefore(topMostModal.$modal);
  }
  private setDefaultFocus() {
    Focus.autoFocus(this.$modal);
  }
  static current(): JQuery {
    return Modal.modalStack.isEmpty
      ? $()
      : Modal.modalStack.peek().$modal;
  }
  static open(options: ModalOptions): void {
    let modal = new Modal(options);

    if (!Modal.modalStack.isEmpty) {
      let topModal = Modal.modalStack.peek();
      let isAlreadyOpen = topModal.id == modal.id;
      if (isAlreadyOpen) {
        modal.rememberPosition(topModal.position);
        modal.rememberTitle(topModal.title);
        modal.rememberPrimaryCaption(topModal.primaryCaption);
        topModal.close();
      }
    }
    modal.open();

    this.wireupDismiss();
    this.wireupSubmit();
  }
  static openDisconnectModal(): void {
    this.open({
      content: '<div id="error-modal" data-modal-id="error-modal" class="modal-md"><div class="modal-header"><h2 class="modal-title">Disconnected</h2><a class="button button-icon  modal-title-close" data-dismiss="on">&#10006;</a></div>' +
        '<div class="modal-content error-details spacer-all"><p>The connection to the application has been lost.' +
        '  Please attempt to restore your internet connection and try the current operation again.</p></div>' +
        '<div class="modal-actions"><div class="modal-primary-actions"><a class="button button-raised button-primary" href="javascript:;" data-dismiss="on">Close</a></div></div></div>'
    });
  }
  static openErrorModal(error: string): void {
    this.open({
      content: '<div id="error-modal" data-modal-id="error-modal" class="modal-md"><div class="modal-header"><h2 class="modal-title">Action Failed</h2><a class="button button-icon modal-title-close" data-dismiss="on">&#10006;</a></div>' +
        '<div class="modal-content error-details spacer-all"><p class="exception">Error: ' + error + '</p></div>' +
        '<div class="modal-actions"><div class="modal-primary-actions"><a class="button button-raised button-primary" href="javascript:;" data-dismiss="on">Close</a></div></div></div>'
    });
  }
  static close(): void {
    if (Modal.modalStack.isNotEmpty)
      Modal.modalStack.peek().close();

    if (Modal.modalStack.isNotEmpty)
      Modal.modalStack.peek().setDefaultFocus();
    else {
      this.unhookDismiss();
      this.unhookSubmit();
    }
  }
  static closeIfCanDismissFromOverlay(): void {
    if (Modal.modalStack.isEmpty)
      return;

    let modal = Modal.modalStack.peek();
    if (modal.isDismissableFromOverlay)
      modal.close();
  }
  static closeAll(): void {
    while (!Modal.modalStack.isEmpty) {
      Modal.modalStack.peek().close();
    }
    this.unhookDismiss();
    this.unhookSubmit();
  }
  static wireupDismiss(): void {
    this.unhookDismiss();
    $(document).on('keyup.dismiss.rustig.modal', (e: JQueryEventObject) => {
      e.which == KeyCode.Escape && Modal.close();
    });
  }
  static unhookDismiss(): void {
    $(document).off('keyup.dismiss.rustig.modal');
  }
  static wireupSubmit(): void {
    this.unhookSubmit();
    $(document).on('keyup.performdefault.rustig.modal', (e: JQueryEventObject) => {
      if (e.ctrlKey && e.which == KeyCode.Enter) {
        if (Modal.modalStack.isNotEmpty) {
          let performed = Modal.modalStack.peek().performDefault();
          if (!performed) {
            Modal.close();
          }
        }
      }
    });
  }
  static unhookSubmit(): void {
    $(document).off('keyup.performdefault.rustig.modal');
  }

  public static initialize: Function = _.once(() => {
    if (!Modal.modalStack)
      Modal.modalStack = new ModalStack();

    $(document).on('click.dismiss.rustig.modal', '.modal-container [data-dismiss]', function (e) {
      e.preventDefault();
      Modal.close();
    });

    $(document).on('click.dismiss.rustig.modal', '#modalGlobalOverlay', function (e) {
      Modal.closeIfCanDismissFromOverlay();
    });
  });
}


class ModalStack {
  modalStack = [];

  get isEmpty(): boolean {
    return this.modalStack.length <= 0;
  }
  get isNotEmpty(): boolean {
    return !this.isEmpty;
  }
  push(modal: Modal) {
    this.modalStack.push(modal);
  }
  pop(): Modal {
    return this.modalStack.pop();
  }
  peek(): Modal {
    if (this.isEmpty)
      return null;
    return this.modalStack[this.modalStack.length - 1];
  }
}

// MODAL INTERFACES
export interface ModalOptions {
  content: any;
  $element?: JQuery;
  alignment?: string;
  onShown?($modal: JQuery): void;
  onHidden?($modal: JQuery): void;
}

export default Modal;
